/**
 * Manual transaction entry. There is no CSV import in v1 — Brian keys receipts
 * in by hand, so this form is the product.
 *
 * The speed decisions:
 *  - date defaults to today and stays put between entries (a stack of receipts
 *    is usually the same day or two)
 *  - description autocompletes from merchants already entered
 *  - the category pre-fills from the server's rule match (GET /transactions/suggest)
 *    the moment the description stops changing — you only touch it when it's wrong
 *  - the account sticks between entries and pre-fills from the last time this
 *    merchant was entered
 *  - Enter saves and returns the cursor to the description field for the next one
 *  - the last few entries stay on screen, with Undo, so you can key without doubt
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { BudgetCategory, Transaction } from '../../shared/types';
import { api, money, parseMoney } from '../../lib/api';
import { todayISO } from './dates';
import { buttonClass, inputClass, labelClass } from './ui';

interface Props {
  categories: BudgetCategory[];
  /** Recent transactions, used to autocomplete merchants and accounts. */
  history: Transaction[];
  onAdded: (t: Transaction) => void;
  onUndone: (t: Transaction) => void;
  onError: (message: string) => void;
}

interface MerchantMemory {
  description: string;
  source_account: string | null;
}

export default function QuickAddForm({
  categories,
  history,
  onAdded,
  onUndone,
  onError,
}: Props) {
  const ids = useId();
  const descriptionRef = useRef<HTMLInputElement>(null);

  const [date, setDate] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [account, setAccount] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Transaction[]>([]);
  const [undoing, setUndoing] = useState<number | null>(null);

  /** Distinct merchants, most recent first, with the account last used for each. */
  const merchants = useMemo(() => {
    const seen = new Map<string, MerchantMemory>();
    for (const t of history) {
      const key = t.description.toLowerCase();
      if (!seen.has(key)) seen.set(key, { description: t.description, source_account: t.source_account });
    }
    return [...seen.values()].slice(0, 200);
  }, [history]);

  const accounts = useMemo(() => {
    const seen = new Set<string>();
    for (const t of history) if (t.source_account) seen.add(t.source_account);
    return [...seen];
  }, [history]);

  const cents = parseMoney(amount);
  const preview = amount.trim() !== '' && cents !== null && cents !== 0 ? money(cents) : null;

  /**
   * Whether the category currently in the select was put there by the rules and not
   * by a human. A ref as well as state because the suggest effect has to read it
   * without re-running every time it flips.
   */
  const autoFilledRef = useRef(false);

  /**
   * Drop an auto-filled category. Called when the merchant changes to one the rules
   * don't know: the previous merchant's category must NOT ride along. Leaving it
   * there quietly mis-categorized the transaction and — worse — taught the rules
   * table a permanent lie ("lowes hardware -> Groceries") on save.
   * A category the human chose is never touched.
   */
  function clearAutoFill() {
    if (!autoFilledRef.current) return;
    autoFilledRef.current = false;
    setAutoFilled(false);
    setCategoryId('');
  }

  function fillFromRule(id: number) {
    autoFilledRef.current = true;
    setAutoFilled(true);
    setCategoryId(String(id));
  }

  // Ask the server which category its rules say this merchant is. Debounced, and
  // never fights a category the human chose by hand.
  useEffect(() => {
    const text = description.trim();
    if (categoryTouched) return; // the human owns the field now

    // Nothing to match on yet — and nothing to inherit from the last merchant.
    if (text.length < 2) {
      clearAutoFill();
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .get<{ category_id: number | null }>(
          `/transactions/suggest?description=${encodeURIComponent(text)}`
        )
        .then((res) => {
          if (cancelled) return;
          const known =
            res.category_id !== null && categories.some((c) => c.id === res.category_id);
          // No rule for this merchant (or it points at a retired category): clear,
          // don't coast on whatever the last merchant filled in.
          if (!known) clearAutoFill();
          else fillFromRule(res.category_id as number);
        })
        .catch(() => {
          /* a missed suggestion is not an error worth interrupting entry for */
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, categoryTouched, categories]);

  function onDescriptionChange(value: string) {
    setDescription(value);

    // Picked an existing merchant out of the list: remember how it was paid for.
    const match = merchants.find((m) => m.description.toLowerCase() === value.toLowerCase());
    if (match && !account && match.source_account) setAccount(match.source_account);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;

    const text = description.trim();
    if (!text) {
      setProblem('Give it a description — the merchant name is enough.');
      descriptionRef.current?.focus();
      return;
    }
    if (cents === null || cents === 0) {
      setProblem('Enter an amount, like 24.99. Use a minus sign for a refund.');
      return;
    }

    setSaving(true);
    setProblem(null);
    try {
      const saved = await api.post<Transaction>('/transactions', {
        occurred_on: date,
        description: text,
        amount: cents,
        source_account: account.trim() || null,
        category_id: categoryId === '' ? null : Number(categoryId),
        notes: null,
      });

      onAdded(saved);
      setJustAdded((prev) => [saved, ...prev].slice(0, 4));

      // Keep the date and the account — the next receipt is usually the same
      // card, same day. Everything else clears for the next one.
      setDescription('');
      setAmount('');
      setCategoryId('');
      setCategoryTouched(false);
      autoFilledRef.current = false;
      setAutoFilled(false);
      descriptionRef.current?.focus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save that one.';
      setProblem(message);
      onError(message);
    } finally {
      setSaving(false);
    }
  }

  async function undo(t: Transaction) {
    setUndoing(t.id);
    try {
      await api.del<Transaction>(`/transactions/${t.id}`);
      setJustAdded((prev) => prev.filter((x) => x.id !== t.id));
      onUndone(t);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not undo that.');
    } finally {
      setUndoing(null);
    }
  }

  const categoryName = (id: number | null) =>
    id === null ? 'Uncategorized' : (categories.find((c) => c.id === id)?.name ?? 'Uncategorized');

  return (
    <section className="rounded-card border border-border bg-surface p-5" aria-labelledby={`${ids}-h`}>
      <h2 id={`${ids}-h`} className="font-display text-lg font-semibold text-ink">
        Add a transaction
      </h2>
      <p className="mt-1 text-sm text-ink-soft">
        Enter saves and jumps back to the merchant, so you can key a stack of receipts without
        leaving the keyboard.
      </p>

      <form onSubmit={submit} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-12">
        <div className="sm:col-span-3">
          <label className={labelClass} htmlFor={`${ids}-date`}>
            Date
          </label>
          <input
            id={`${ids}-date`}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-6">
          <label className={labelClass} htmlFor={`${ids}-desc`}>
            Merchant / description
          </label>
          <input
            id={`${ids}-desc`}
            ref={descriptionRef}
            type="text"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            list={`${ids}-merchants`}
            autoComplete="off"
            placeholder="Harris Teeter"
            className={inputClass}
          />
          <datalist id={`${ids}-merchants`}>
            {merchants.map((m) => (
              <option key={m.description} value={m.description} />
            ))}
          </datalist>
        </div>

        <div className="sm:col-span-3">
          <label className={labelClass} htmlFor={`${ids}-amount`}>
            Amount
          </label>
          <input
            id={`${ids}-amount`}
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="24.99"
            aria-describedby={preview ? `${ids}-amount-preview` : undefined}
            className={inputClass}
          />
          {preview && (
            <p id={`${ids}-amount-preview`} className="mt-1 text-xs text-ink-soft">
              {cents !== null && cents < 0 ? `Refund of ${money(Math.abs(cents))}` : preview}
            </p>
          )}
        </div>

        <div className="sm:col-span-4">
          <label className={labelClass} htmlFor={`${ids}-cat`}>
            Category
          </label>
          <select
            id={`${ids}-cat`}
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setCategoryTouched(true);
              autoFilledRef.current = false;
              setAutoFilled(false);
            }}
            className={inputClass}
          >
            <option value="">Uncategorized</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-soft">
            {autoFilled ? (
              <span className="text-sage">
                <span aria-hidden="true">✓ </span>Filled in from a past entry
              </span>
            ) : (
              'Pick one and it will fill itself in next time'
            )}
          </p>
        </div>

        <div className="sm:col-span-4">
          <label className={labelClass} htmlFor={`${ids}-account`}>
            Account <span className="font-normal text-ink-faint">(optional)</span>
          </label>
          <input
            id={`${ids}-account`}
            type="text"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            list={`${ids}-accounts`}
            autoComplete="off"
            placeholder="Amex"
            className={inputClass}
          />
          <datalist id={`${ids}-accounts`}>
            {accounts.map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
        </div>

        <div className="flex items-end sm:col-span-4">
          <button type="submit" disabled={saving} className={`${buttonClass} w-full`}>
            {saving ? 'Saving…' : 'Add transaction'}
          </button>
        </div>
      </form>

      <p aria-live="polite" className="mt-3 min-h-[1.25rem] text-sm text-over">
        {problem && (
          <>
            <span aria-hidden="true">⚠ </span>
            {problem}
          </>
        )}
      </p>

      {justAdded.length > 0 && (
        <div className="mt-2 border-t border-border pt-3">
          <h3 className="text-xs font-medium tracking-wide text-ink-soft">Just added</h3>
          <ul className="mt-2 space-y-1">
            {justAdded.map((t) => (
              <li
                key={t.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-sage-soft px-3 py-2 text-sm"
              >
                <span className="min-w-0 truncate text-ink">
                  <span aria-hidden="true">✓ </span>
                  {t.description} · {money(t.amount)} · {categoryName(t.category_id)}
                </span>
                <button
                  type="button"
                  onClick={() => undo(t)}
                  disabled={undoing === t.id}
                  className="min-h-[44px] shrink-0 rounded-lg px-2 text-sm font-medium text-sage underline underline-offset-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50 disabled:opacity-60"
                >
                  {undoing === t.id ? 'Undoing…' : 'Undo'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
